import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  type Messaging,
  type MessagePayload,
  type Unsubscribe,
} from 'firebase/messaging';
import {
  FCM_TOKEN_STORAGE_KEY,
  firebaseConfig,
  isFirebaseConfigured,
} from '@/shared/lib/firebase/config';

let app: FirebaseApp | null = null;
let messaging: Messaging | null = null;

async function getMessagingInstance(): Promise<Messaging | null> {
  if (!isFirebaseConfigured()) {
    console.warn('[fcm] Firebase env is not configured');
    return null;
  }

  if (typeof window === 'undefined') return null;
  if (!(await isSupported())) {
    console.warn('[fcm] Firebase Messaging is not supported in this browser');
    return null;
  }

  if (!app) {
    app = initializeApp(firebaseConfig);
  }
  if (!messaging) {
    messaging = getMessaging(app);
  }
  return messaging;
}

/**
 * Request notification permission, register the FCM service worker, and return
 * the device token. Caller is responsible for posting it to the backend.
 */
export async function requestForToken(): Promise<string | null> {
  try {
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) return null;

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('[fcm] Notification permission denied');
      return null;
    }

    const encodedConfig = encodeURIComponent(JSON.stringify(firebaseConfig));
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js?firebaseConfig=${encodedConfig}`,
    );
    await navigator.serviceWorker.ready;

    try {
      await registration.pushManager.getSubscription();
    } catch (pushErr) {
      console.error('[fcm] Browser PushManager failed:', pushErr);
    }

    const currentToken = await getToken(messagingInstance, {
      vapidKey: import.meta.env.VITE_VAPIDKEY,
      serviceWorkerRegistration: registration,
    });

    if (!currentToken) {
      console.warn('[fcm] No registration token available');
      return null;
    }

    return currentToken;
  } catch (err) {
    console.error('[fcm] Error retrieving token:', err);
    return null;
  }
}

/** Listen for foreground FCM messages. Returns unsubscribe, or null if unavailable. */
export async function onMessageListener(
  callback: (payload: MessagePayload) => void,
): Promise<Unsubscribe | null> {
  try {
    const messagingInstance = await getMessagingInstance();
    if (!messagingInstance) return null;
    return onMessage(messagingInstance, callback);
  } catch (err) {
    console.error('[fcm] Failed to attach foreground listener:', err);
    return null;
  }
}

export function clearCachedFcmToken(): void {
  localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
}

export function getCachedFcmToken(): string | null {
  return localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
}

export function setCachedFcmToken(token: string): void {
  localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
}
